import { CommonModule } from '@angular/common';
import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import {extractErrorMessage, goBack} from 'src/app/common/common';
import {ChannelDto, ConversionViewModel, PowerTypesViewModel, siScaleCodesViewModel} from 'src/app/model/channel.model';
import { ChannelViewModel } from 'src/app/model/channel.model';
import { AuthService } from 'src/app/services/auth.service';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { SearchFilterModule } from '../../../common/searchFilter/searchFilter.module';

@Component({
  selector: 'app-create-channel',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule, SearchFilterModule],
  templateUrl: './create-channel.component.html',
  styleUrl: './create-channel.component.scss'
})
export class CreateChannelComponent implements OnInit, OnDestroy {
  //search
  protected searchText: string = '';
  //form
  protected channelConversionForm = new FormGroup({
    id: new FormControl<number>(0, [Validators.required, Validators.pattern('[0-9]*')]),
    displayLabel: new FormControl<string>('', [Validators.required]),
    itemUnit: new FormControl<string>('', [Validators.required]),
    siScaleCode: new FormControl<string>('', [Validators.required])
  });

  protected unsubscribe = new Subject<void>();
  protected formsSubscribe: any;
  protected isAdmin: boolean = false;
  protected editing: boolean = false;
  protected error: string | null = null;
  protected platformId: number;
  protected router: Router;
  protected item_units: string[] = [];

  protected channelViewModel: ChannelViewModel;
  protected channelDto: ChannelDto = {
    id: 0,
  };

  protected si_scale_codes: siScaleCodesViewModel[] = [];
  protected displayedChannelPowerTypes: any[] = [];
  protected conversions: ConversionViewModel[] = [];
  protected powerTypes: PowerTypesViewModel[] = [];

  constructor(
    private injector: Injector,
    private authService: AuthService,
    private createChannelService: ApiService
  ) {
    this.router = injector.get(Router);
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.editing = true;
        this.platformId = event.snapshot.params.id;
      }
    });
  }

  setFormsDefaultValue(){
    this.channelConversionForm.controls.id.setValue(this.channelViewModel.id);
    this.channelConversionForm.controls.displayLabel.setValue(this.channelViewModel.display_label);
    this.channelConversionForm.controls.itemUnit.setValue(this.channelViewModel.itemUnit);
    this.channelConversionForm.controls.siScaleCode.setValue(this.channelViewModel.siScaleCode);

    if (this.editing) {
      this.channelConversionForm.controls.id.disable();
    }
  }

  getAll() {
    forkJoin([
      this.createChannelService.getConversion({ depth: 1 }).pipe((takeUntil(this.unsubscribe))),
      this.createChannelService.getSiScaleCode().pipe((takeUntil(this.unsubscribe))),
      this.createChannelService.getItemUnits().pipe((takeUntil(this.unsubscribe))),
      this.createChannelService.getPowerType({ depth: 1 }).pipe((takeUntil(this.unsubscribe))),
    ]).subscribe({
      next: ([conversion, siScaleCode, itemUnits, powerType]) => {
        if(conversion) {
          this.conversions = conversion.map((conver: any) => conver);
        }
        if(siScaleCode) {
          this.si_scale_codes = siScaleCode.map((si: any) => si);
        }
        if(itemUnits) {
          this.item_units = itemUnits.map((item: any) => item);
        }
        if(powerType) {
          this.powerTypes = powerType.map((power: any) => power);
        }

        // If editing, get the specific channel data
        if (this.editing) {
          const channelQueryParams: CustomHttpParams = {
            'depth': 3
          };
          this.createChannelService.getChannel(channelQueryParams).pipe((takeUntil(this.unsubscribe))).subscribe({
            next: (response: ChannelViewModel[]) => {
              const foundChannel = response.find((channel: ChannelViewModel) => channel.id === Number(this.platformId));
              if (foundChannel) {
                this.channelViewModel = foundChannel;

                // Ensure channel_powertypes array exists
                if (!this.channelViewModel.channel_powertypes) {
                  this.channelViewModel.channel_powertypes = [];
                }

                this.displayedChannelPowerTypes = [...this.channelViewModel.channel_powertypes];
                this.setFormsDefaultValue();
              }
            },
            error: (err) => {
              console.error(err);
              this.error = extractErrorMessage(err, "Failed to load channel data.");
            }
          });
        } else {
          // Initialize empty array for new channel
          this.displayedChannelPowerTypes = [];
        }
      },
      error: (err) => {
        console.error(err);
        this.error = extractErrorMessage(err, "Failed to load required data.");
      }
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.getAll();

    this.formsSubscribe = this.channelConversionForm.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(
      (val: any) => {
        if (val.id) this.channelDto.id = val.id;
        if (val.displayLabel) this.channelDto.display_label = val.displayLabel;
        if (val.itemUnit) this.channelDto.itemUnit = val.itemUnit;
        if (val.siScaleCode) this.channelDto.siScaleCode = val.siScaleCode;
      }
    );
  }

  save(){
    if (this.channelConversionForm.invalid) {
      this.error = 'Please fill out all fields correctly and try again.';
      return;
    }

    if (this.getInvalidChannelPowerTypes().length > 0) {
      this.error = 'Please select both Conversion and Powertype for all channel conversions before saving.';
      return;
    }

    // Update channel object with form values
    if (this.editing) {
      this.channelDto.channel_powertypes = this.displayedChannelPowerTypes;
      this.createChannelService.editChannel(this.channelDto).subscribe({
        next: () => {
          goBack('/channels', this.router);
        },
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err, "Failed to update channel.");
        }
      });
    } else {
      this.channelDto.channel_powertypes = this.displayedChannelPowerTypes;
      this.createChannelService.createChannel(this.channelDto).subscribe({
        next: () => {
          goBack('/channels', this.router);
        },
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err, "Failed to create channel.");
        }
      });
    }
  }

  private getInvalidChannelPowerTypes() {
    // Validate that all displayedChannelPowerTypes have both powertype and conversion
    return this.displayedChannelPowerTypes.filter((item, index) => {
      const hasValidPowertype = item.powertype &&
        item.powertype.itemDescription &&
        item.powertype.itemDescription.trim() !== '';

      const hasValidConversion = item.conversion &&
        item.conversion.id &&
        item.conversion.id.toString().trim() !== '';

      if (!hasValidPowertype || !hasValidConversion) {
        console.log(`Invalid item at index ${index}:`, {
          powertype: item.powertype,
          conversion: item.conversion,
          hasValidPowertype,
          hasValidConversion
        });
        return true;
      }
      return false;
    });
  }

  backPage(url: string, router: Router){
    goBack(url, router);
  }

  setSiScaleCode(event: any) {
    this.channelDto.siScaleCode = event.value;
    this.channelConversionForm.controls.siScaleCode.setValue(event.value);
  }

  setItem(event: any) {
    this.channelDto.itemUnit = event.value;
    this.channelConversionForm.controls.itemUnit.setValue(event.value);
  }

  setPowerType(event: any, idx: any) {
    const powertype = this.powerTypes.find((pt: any) => pt.itemDescription === event.value);
    if (powertype) {
      this.displayedChannelPowerTypes[idx].powertype = powertype;
    }
  }

  setConversion(event: any, idx: any) {
    const conversion = this.conversions.find((conver: any) => conver.id === Number(event.value));
    if (conversion) {
      this.displayedChannelPowerTypes[idx].conversion = conversion;
    }
  }

  deleteConversion(idx: any) {
    this.displayedChannelPowerTypes.splice(idx, 1);
  }

  addConversion(){
    this.displayedChannelPowerTypes.push({
      powertype: null,
      conversion: null,
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
